import '@grapecity/wijmo.styles/wijmo.css';
import './assets/css/tkts-base.css';
import './assets/css/mui-custom.css';
import './assets/css/wijmo-custom.css';

import * as wjcCore from '@grapecity/wijmo';
import '@grapecity/wijmo.cultures/wijmo.culture.ja';
import axios, {HttpStatusCode} from 'axios';
import PropTypes from 'prop-types';
import React from 'react';
import 'react-app-polyfill/ie11';
import {createRoot} from 'react-dom/client';
import {IntlProvider} from 'react-intl';
import {connect, Provider as ReduxProvider} from 'react-redux';
import {BrowserRouter} from 'react-router-dom';
import {PersistGate} from 'redux-persist/integration/react';
import {getResponseMethod, isInvalidResponse} from './common/backend';
import Conf from './config/config.js';
import GlobalIntlProvider from './core/globalIntl';
import ErrorBoundary from './pages/ErrorBoundary';
import {authOperations} from './reducks/auth';
import {commonOperations} from './reducks/common';
import configureStore, {persistor} from './reducks/store';
import ContainerRouter from './router';
import * as serviceWorker from './serviceWorker';

wjcCore.setLicenseKey(Conf.WIJMO_LICENSE_KEY);

const reduxStore = configureStore(window.REDUX_INITIAL_DATA);
const {dispatch} = reduxStore;

let MainComponent = ({locale, messages}) => (
  <IntlProvider locale={locale} messages={messages}>
    <GlobalIntlProvider>
      <ContainerRouter />
    </GlobalIntlProvider>
  </IntlProvider>
);

MainComponent = connect((state) => ({
  locale: state.style.locale,
  messages: state.style.messages,
}))(MainComponent);

// 言語切り替えが実装したい場合は、local / messages を
// redux の store で管理するようにし、動的に入れ替える。
// Material-ui のテーマについても同様、Provider に与える
// テーマを事前に複数用意しておき、ユーザ操作によりその値
// を変更することで、複数パターンの動的テーマ切り替えを可能とする。

const root = createRoot(document.getElementById('root'));
root.render(
    <ReduxProvider store={reduxStore}>
      <ErrorBoundary>
        <PersistGate loading={null} persistor={persistor}>
          <BrowserRouter>
            <MainComponent/>
          </BrowserRouter>
        </PersistGate>
      </ErrorBoundary>
    </ReduxProvider>,
);

// ServiceWorker を利用する場合にはこちらの利用を検討する。
// デフォルトでは unregister なので利用しない。
// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();

// Add a request interceptor
axios.interceptors.request.use(async function(config) {
  // 画面表示タイムアウト値の再設定
  dispatch(commonOperations.doCheckAccess());

  // トークンの更新
  await dispatch(authOperations.doRefreshTokenOperation());

  if (reduxStore.getState().auth?.loginInfo) {
    const headers = config.headers;

    headers['Authorization'] = reduxStore.getState().auth.loginInfo.idToken;
    headers['Content-Type'] = 'application/json';
  }

  // カスタム設定 通信中画像表示有無を取得
  const useLoadingImage = config.custom?.useLoadingImage;

  // Do something before request is sent
  // styleAction.startRequest(); -> リクエストの開始を意味する。isRequesting = true にする。
  const method = config.method;
  console.info(
      `API ${method?.toUpperCase()}`,
      config.url,
      'REQUEST\n',
      config.data,
  );

  if (useLoadingImage !== false && ['get', 'post', 'put', 'delete'].includes(method)) {
    dispatch(commonOperations.startRequest());
  }

  return config;
}, function(error) {
  // Do something with request error
  // styleAction.endRequest(); -> リクエストの終了を意味する。isRequesting = false にする。

  console.log('axios.interceptors END ERR1');
  dispatch(commonOperations.endRequest());
  errorDialog(error);
  return Promise.reject(error);
});

// Add a response interceptor
axios.interceptors.response.use(function(response) {
  try {
    // Do something with response data
    // styleAction.endRequest(); -> リクエストの終了を意味する。isRequesting = false にする。
    const method = getResponseMethod(response);
    console.info(
        `API ${method?.toUpperCase()}`,
        response.request?.responseURL,
        'RESPONSE\n',
        response,
    );

    if (['get', 'post', 'put', 'delete'].includes(method)) {
      dispatch(commonOperations.endRequest());
    }

    if (isInvalidResponse(response)) {
      const error = new Error('Server unhandled Error');
      console.error(error);
      return Promise.reject(error);
    } else {
      return Promise.resolve(response);
    }
  } catch (e) {
    console.error(e);
    return Promise.reject(e);
  }
}, function(error) {
  // Do something with response error
  // styleAction.endRequest(); -> リクエストの終了を意味する。isRequesting = false にする。
  dispatch(commonOperations.endRequest());
  errorDialog(error);

  return Promise.reject(error);
});

// 通信時のエラーメッセージを設定
function errorDialog(error) {
  const url = error.config?.url;
  const method = error.config?.method?.toUpperCase();

  // エラーログ出力
  console.error(`API ${method} failure`, url, error.message, '\n', error);

  // デフォルトはシステムエラー
  let errDialog = {
    message: 'TKSCS00027S',
    highPriority: true,
  };

  // レスポンス時のエラー
  const response = error.response;
  if (response) {
    // HTTPステータス403の場合 認証/認可エラー
    if (response.status == HttpStatusCode.Forbidden) {
      // ユーザーが無効の場合とそれ以外でメッセージを分ける
      const msg = response.data.errorCode === 'expired' ? 'TKSCZ00022E' : 'TKSCZ00025E';

      errDialog = {
        message: msg,
        highPriority: true,
      };
    }

    // NOTE: 他のステータスでエラーメッセージを制御する場合はここに処理を追加
  }

  dispatch(commonOperations.doShowMessage(errDialog));
}

MainComponent.propTypes = {
  locale: PropTypes.string,
  messages: PropTypes.object,
};
